// StudySpot 목업 데이터

import type { Space, User, Reservation, Resource, Question, Seat } from '../types';

// 공간 정보
export const SPACES: Space[] = [
  {
    id: 'focus',
    name: '1인 포커스룸',
    capacity: 10,
    theme: 'purple',
    description: '집중이 필요할 때, 완벽한 1인 공간 (10석)'
  },
  {
    id: 'group-a',
    name: '스터디룸 A',
    capacity: 4,
    theme: 'orange',
    description: '4인 그룹 스터디룸'
  },
  {
    id: 'group-b',
    name: '스터디룸 B',
    capacity: 4,
    theme: 'orange',
    description: '4인 그룹 스터디룸'
  },
  {
    id: 'open',
    name: '오픈석',
    capacity: 30,
    theme: 'green',
    description: '자유로운 분위기의 열린 공간 (30석)'
  }
];

// 좌석 데이터 생성
const generateSeats = (): Seat[] => {
  const seats: Seat[] = [];

  // 포커스룸 10석
  for (let i = 1; i <= 10; i++) {
    seats.push({
      id: `focus-${i}`,
      number: i,
      space_id: 'focus',
      status: i <= 6 ? 'occupied' : 'available' // 6석 사용중
    });
  }

  // 스터디룸 A (4석)
  for (let i = 1; i <= 4; i++) {
    seats.push({
      id: `group-a-${i}`,
      number: i,
      space_id: 'group-a',
      status: i <= 2 ? 'occupied' : 'available' // 2석 사용중
    });
  }

  // 스터디룸 B (4석)
  for (let i = 1; i <= 4; i++) {
    seats.push({
      id: `group-b-${i}`,
      number: i,
      space_id: 'group-b',
      status: 'available' // 모두 비어있음
    });
  }

  // 오픈석 30석
  for (let i = 1; i <= 30; i++) {
    seats.push({
      id: `open-${i}`,
      number: i,
      space_id: 'open',
      status: i <= 18 ? 'occupied' : 'available' // 18석 사용중
    });
  }

  return seats;
};

export const MOCK_SEATS: Seat[] = generateSeats();

// 회원 목록 (데모용)
export const MOCK_USERS: User[] = [
  { id: '1', nickname: '공부하는민수' },
  { id: '2', nickname: '수학왕지은' },
  { id: '3', nickname: '코딩마스터' },
  { id: '4', nickname: '새벽공부러' },
  { id: '5', nickname: '열공학생' }
];

// 예약 목록 (데모용 - 당일 예약만)
export const MOCK_RESERVATIONS: Reservation[] = [];

// 학습 자료 (즐겨찾기) - 고등학생 대상
export const MOCK_RESOURCES: Resource[] = [
  { id: '1', name: '메가스터디', url: 'https://megastudy.net', is_favorite: true },
  { id: '2', name: '대성마이맥', url: 'https://mimacstudy.com', is_favorite: true },
  { id: '3', name: 'EBSi', url: 'https://ebsi.co.kr', is_favorite: true },
  { id: '4', name: '이투스', url: 'https://etoos.com', is_favorite: false },
  { id: '5', name: '스카이에듀', url: 'https://skyedu.com', is_favorite: false },
  { id: '6', name: '수학의 정석 풀이', url: 'https://mathbook.kr', is_favorite: false },
  { id: '7', name: '오르비', url: 'https://orbi.kr', is_favorite: true }
];

// Q&A 질문게시판
export const MOCK_QUESTIONS: Question[] = [
  {
    id: '1',
    user_nickname: '수학왕지은',
    image_url: '/demos/study-spot/qna-math.png',
    question: '이 적분 문제 어떻게 푸나요? 치환적분인 것 같은데...',
    created_at: '2025-11-27T10:30:00',
    is_solved: false,
    answers: [
      {
        id: 'a1',
        user_nickname: '공부하는민수',
        content: 'u=x²로 치환하면 돼요! du=2x dx 이니까 적분 범위도 바꿔주세요.',
        created_at: '2025-11-27T10:45:00'
      },
      {
        id: 'a2',
        user_nickname: '코딩마스터',
        content: '저도 이거 헷갈렸는데 위 답변 맞아요 👍',
        created_at: '2025-11-27T11:00:00'
      }
    ]
  },
  {
    id: '2',
    user_nickname: '새벽공부러',
    image_url: '/demos/study-spot/qna-english.png',
    question: '이 영어 문법 문제 해석이 안되는데 도와주세요',
    created_at: '2025-11-26T22:00:00',
    is_solved: true,
    answers: [
      {
        id: 'a3',
        user_nickname: '수학왕지은',
        content: '관계대명사 that이 생략된 거예요. 선행사가 사물이라서요.',
        created_at: '2025-11-26T22:30:00'
      }
    ]
  },
  {
    id: '3',
    user_nickname: '열공학생',
    image_url: '/demos/study-spot/qna-physics.png',
    question: '물리 문제인데요, 이 자유낙하 문제에서 시간 구하는 방법을 모르겠어요',
    created_at: '2025-11-25T15:20:00',
    is_solved: false,
    answers: []
  }
];

// 이용 시간대 옵션
export const TIME_SLOTS = [
  { value: '09:00-13:00', label: '오전 (09:00-13:00)' },
  { value: '13:00-18:00', label: '오후 (13:00-18:00)' },
  { value: '18:00-22:00', label: '저녁 (18:00-22:00)' }
];

// 이용 목적 옵션
export const PURPOSE_OPTIONS = [
  '개인 학습',
  '그룹 스터디',
  '시험 준비',
  '업무',
  '독서',
  '기타'
];
